import { useState } from "react";
import { toast } from "sonner";
import { X, Package } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { UkDateInput } from "@/components/UkDateInput";

const BUNDLES = { "Weekly companionship bundle": 1, "Respite bundle": 3 };
const input = "w-full rounded-lg border border-border bg-white px-3 py-2 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/20";
const addDays = (d, n) => { const x = new Date(`${d}T12:00:00`); x.setDate(x.getDate() + n); return x; };
const endOf = (t, h) => { const [a, b] = t.split(":").map(Number); const m = a * 60 + b + h * 60; return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`; };

export function BundleBookForm({ clients, staff, defaultDate, onClose, onSaved }) {
  const [f, setF] = useState({ client_id: "", carer_id: "", service: "Weekly companionship bundle", first_date: defaultDate, start_time: "10:00" });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const book = async (override) => {
    if (!f.client_id || !f.carer_id) { toast.error("Select a client and befriender"); return; }
    setBusy(true);
    try {
      const r = await api.post("/visits/bundle", { ...f, override });
      setResult(r.data);
      if (r.data.created.length) { toast.success(`${r.data.created.length} of 4 sessions booked`); onSaved(); }
      if (!r.data.failed.length) onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const hrs = BUNDLES[f.service];
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div data-testid="bundle-form" className="relative z-10 max-h-[92vh] w-full max-w-md overflow-y-auto rounded-xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-ink"><Package size={20} className="text-brand" /> Book a bundle</h2>
          <button type="button" onClick={onClose} data-testid="bundle-close"><X size={20} className="text-ink-muted" /></button>
        </div>
        <div className="space-y-3">
          <select className={input} value={f.service} onChange={set("service")} data-testid="bundle-service-select">
            {Object.keys(BUNDLES).map((b) => <option key={b} value={b}>{b} ({b === "Respite bundle" ? "4 x 3 hours" : "4 x 1 hour"})</option>)}
          </select>
          <select className={input} value={f.client_id} onChange={set("client_id")} data-testid="bundle-client-select">
            <option value="">Choose client</option>{clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select className={input} value={f.carer_id} onChange={set("carer_id")} data-testid="bundle-carer-select">
            <option value="">Choose befriender (same for all 4)</option>{staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="mb-1 block text-xs font-medium text-ink">First session</label>
              <UkDateInput testid="bundle-first-date" clearable={false} value={f.first_date} onChange={(v) => v && setF({ ...f, first_date: v })} /></div>
            <div><label className="mb-1 block text-xs font-medium text-ink">Start time</label>
              <input type="time" className={input} value={f.start_time} onChange={set("start_time")} data-testid="bundle-start-time" /></div>
          </div>
          <div className="rounded-lg bg-ivory p-3 text-xs text-ink-soft" data-testid="bundle-preview">
            <p className="mb-1 font-semibold text-ink">4 sessions, one each week, {f.start_time}–{endOf(f.start_time, hrs)}:</p>
            {[0, 7, 14, 21].map((n) => <p key={n}>• {addDays(f.first_date, n).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}</p>)}
          </div>
          {result?.failed?.length > 0 && (
            <div className="rounded-lg border border-coral/40 bg-coral-light p-3 text-xs text-coral-dark" data-testid="bundle-failed">
              <p className="mb-1 font-semibold">Not booked:</p>
              {result.failed.map((x) => <p key={x.date}>• {new Date(`${x.date}T12:00:00`).toLocaleDateString("en-GB")}: {x.reason}</p>)}
              <p className="mt-1">Book those dates separately, or choose another befriender.</p>
            </div>
          )}
        </div>
        <div className="mt-5 flex gap-2">
          <button type="button" onClick={onClose} className="flex-1 rounded-lg border border-border py-2.5 font-medium text-ink hover:bg-secondary">{result ? "Done" : "Cancel"}</button>
          {!result && <button type="button" disabled={busy} onClick={() => book(false)} data-testid="bundle-book-button" className="flex-1 rounded-lg bg-brand py-2.5 font-semibold text-white hover:bg-brand-dark disabled:opacity-50">Book 4 sessions</button>}
        </div>
      </div>
    </div>
  );
}

export function BundleCounter({ bundles }) {
  if (!bundles?.length) return null;
  return (
    <section className="rounded-2xl border border-brand/20 bg-white p-6 shadow-sm" data-testid="bundle-counter">
      <h2 className="mb-3 flex items-center gap-2 font-heading text-lg font-semibold text-ink"><Package size={18} className="text-brand" /> This month's bundle visits</h2>
      <div className="space-y-4">
        {bundles.map((b) => (
          <div key={b.service} data-testid={`bundle-${b.service.replace(/\s+/g, "-").toLowerCase()}`}>
            <p className="text-sm font-semibold text-ink">{b.service} · {new Date(`${b.month}-01T12:00:00`).toLocaleDateString("en-GB", { month: "long", year: "numeric" })}</p>
            <div className="mt-2 flex gap-2">
              {Array.from({ length: b.included }, (_, i) => {
                const tone = i < b.used ? "bg-sage text-white" : i < b.used + b.booked ? "bg-gold-light text-gold-dark" : "border-2 border-dashed border-border text-ink-muted";
                return <span key={i} className={`flex h-9 w-9 items-center justify-center rounded-full text-xs font-bold ${tone}`}>{i + 1}</span>;
              })}
            </div>
            <p className="mt-2 text-sm text-ink-soft" data-testid="bundle-counts"><b className="text-ink">{b.used}</b> used · <b className="text-ink">{b.booked}</b> booked · <b className="text-brand">{b.left}</b> left of {b.included} ({b.hours_each} hour{b.hours_each > 1 ? "s" : ""} each)</p>
          </div>
        ))}
      </div>
    </section>
  );
}
